import { RegistrationData } from '@sportninja/common/hooks/useRegistrationData'
import { Currency } from '@sportninja/common/types'
import { Registration } from '@sportninja/common/types/Registration'
import { Team } from '@sportninja/common/types/Registration'

interface DivisionData {
  id: string
  name: string
  registration: {
    division_age?: {
      name: string
    }
    is_registration_on: number
    division_gender?: {
      name: string
      id: string
    }
    total_players: number
    max_players_per_team: number
    max_teams: number
    deposit: number
    team_cost: number
    total_paid: number
    single_payment: boolean
  }
  teams?: {
    registration_status: number
  }[]
}

export interface RegistrationTableDivision {
  id: string
  name: string
  is_visible_standings: boolean
  type: string
  isSeason: boolean
  open: boolean
  gender: string
  genderId: string
  totalPlayers: number
  totalTeams: number
  maxPlayers: number
  maxTeams: number
  maxPlayersPerBucket: number | null
  deposit: number | null
  cost: number | null
  revenue: number
  teams: Team[] // Consider creating a proper type for teams
  registration: Registration
  currency: Currency
  singlePayment: boolean
  playerCost: number | null
  playerDeposit: number | null
  playerSinglePayment: boolean
  playerNumberOfInstallments: number | null
  playerInstallmentFrequency: string | null
  playerInstallmentStartDate: string | null
  dayOfWeek: {
    id: string
    name: string
    name_full: string | null
    abbreviation: string | null
  } | null
  bucketTeams: number
}

/**
 * Optimized division computation with better performance characteristics
 * @param data - Raw registration data from the API
 * @returns Array of formatted division data
 */
export const computeDivisions = (
  data?: RegistrationData
): RegistrationTableDivision[] | null | undefined => {
  if (!data?.detailed?.divisions) {
    return []
  }

  // Cache frequently accessed values
  const detailedId = data?.detailed?.id
  const isSeasonType = data?.detailed?.type === 'Season'
  const defaultCurrency =
    data.detailed.registration?.currency || getDefaultCurrency()

  return data.detailed.divisions.map((division) => {
    const registeredTeams = division.registered_teams || []
    const registration = division.registration

    // Single pass through teams array for better performance
    let activeTeamCount = 0
    let bucketTeamCount = 0

    for (const team of registeredTeams) {
      if (team?.is_bucket) {
        bucketTeamCount++
      } else if (team.registration_status !== 5) {
        activeTeamCount++
      }
    }

    // Cache registration properties
    const divisionAge = registration?.division_age
    const divisionGender = registration?.division_gender
    const isSinglePayment =
      registration?.single_payment ||
      registration?.player_single_payment ||
      false

    return {
      isSeason: division?.id === detailedId && isSeasonType,
      id: division.id,
      name: division.name,
      is_visible_standings: division?.is_visible_standings,
      type: divisionAge?.name || '',
      open: registration?.is_registration_on === 1,
      gender: divisionGender?.name || '',
      genderId: divisionGender?.id || '',
      totalPlayers: registration?.total_players || 0,
      maxPlayers: registration?.max_players_per_team || 0,
      totalTeams: activeTeamCount,
      maxTeams: registration?.max_teams || 0,
      deposit: registration?.deposit || null,
      depositRequired: !!registration?.deposit_required,
      cost: registration?.team_cost || null,
      playerCost: registration?.player_cost || null,
      playerDeposit: registration?.player_deposit || null,
      playerDepositRequired: !!registration?.player_deposit_required,
      playerRequirePayment: !!registration?.player_require_payment,
      playerNumberOfInstallments:
        registration?.player_number_of_installments || null,
      playerInstallmentFrequency:
        registration?.player_installment_frequency || null,
      playerInstallmentStartDate:
        registration?.player_installment_start_date || null,
      revenue: registration?.total_paid || 0,
      teams: registeredTeams,
      registration: registration,
      currency: defaultCurrency,
      singlePayment: isSinglePayment,
      dayOfWeek: registration?.day_of_week || null,
      playerSinglePayment: registration?.player_single_payment || false,
      maxPlayersPerBucket: registration?.max_players_per_bucket || null,
      bucketTeams: bucketTeamCount,
    }
  })
}

/**
 * Helper function to validate division data
 * @param division - Division data to validate
 * @returns Boolean indicating if division data is valid
 */
export const isValidDivisionData = (
  division: Partial<DivisionData>
): division is DivisionData => {
  return !!(
    division &&
    division.id &&
    division.name &&
    division.registration &&
    typeof division.registration.is_registration_on === 'number'
  )
}

/**
 * Helper function to get default currency object
 * @returns Default currency object
 */
export const getDefaultCurrency = () => ({
  id: '',
  name: '',
  name_full: null,
  abbreviation: null,
})

/**
 * Helper function to calculate active teams count
 * @param teams - Array of team data
 * @returns Number of active teams
 */
export const calculateActiveTeams = (
  teams?: { registration_status: number }[]
): number => {
  if (!teams || !Array.isArray(teams)) {
    return 0
  }
  return teams.filter((team) => team.registration_status !== 5).length
}

/**
 * Helper function to safely get division registration value
 * @param registration - Registration object
 * @param key - Key to access
 * @param defaultValue - Default value if key doesn't exist
 * @returns Value from registration or default
 */
export const getDivisionRegistrationValue = <T,>(
  registration: any,
  key: string,
  defaultValue: T
): T => {
  if (!registration || typeof registration !== 'object') {
    return defaultValue
  }
  return registration[key] ?? defaultValue
}

/**
 * Interface for configuration options
 */
interface TrendPercentageOptions {
  decimalPlaces?: number
  defaultValue?: string
  includeSign?: boolean
}

/**
 * Computes the trend percentage from a given value
 * @param value - The raw trend value (usually between 0 and 1)
 * @param options - Configuration options for the calculation
 * @returns Formatted trend percentage as a string
 */
export const computeTrendPercentage = (
  value?: number | null,
  options: TrendPercentageOptions = {}
): string => {
  const {
    decimalPlaces = 2,
    defaultValue = '0.00',
    includeSign = false,
  } = options

  // Handle invalid inputs
  if (value === null || value === undefined || isNaN(value)) {
    return defaultValue
  }

  try {
    // Convert to percentage and round to specified decimal places
    const percentage = (value * 100).toFixed(decimalPlaces)

    // Handle sign if required
    if (includeSign) {
      const sign = Number(percentage) > 0 ? '+' : ''
      return `${sign}${percentage}`
    }

    return percentage
  } catch (error) {
    console.error('Error computing trend percentage:', error)
    return defaultValue
  }
}

/**
 * Formats the trend direction with an arrow
 * @param value - The trend percentage value
 * @returns String with trend direction arrow
 */
export const formatTrendDirection = (value?: number | null): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '→'
  }

  if (value > 0) {
    return '↑'
  }
  if (value < 0) {
    return '↓'
  }
  return '→'
}

/**
 * Computes trend color based on the value
 * @param value - The trend percentage value
 * @param options - Color options for positive, negative, and neutral values
 * @returns Color string for the trend
 */
export const computeTrendColor = (
  value?: number | null,
  options = {
    positive: '#4CAF50',
    negative: '#F44336',
    neutral: '#9E9E9E',
  }
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return options.neutral
  }

  if (value > 0) {
    return options.positive
  }
  if (value < 0) {
    return options.negative
  }
  return options.neutral
}

/**
 * Creates a complete trend display object
 * @param value - The trend percentage value
 * @returns Object containing formatted value, direction, and color
 */
export const createTrendDisplay = (value?: number | null) => {
  return {
    percentage: computeTrendPercentage(value, { includeSign: true }),
    direction: formatTrendDirection(value),
    color: computeTrendColor(value),
    raw: value,
  }
}
